import { Validators } from '@angular/forms';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { UNITS } from '@ordersnproducts/modules/shared/constants/units';
import { FormInputCheckboxComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-input-checkbox/form-input-checkbox.component';
import { FormInputNumberComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-input-number/form-input-number.component';
import {
  FieldConfig,
  NewLineField,
} from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { FieldInputNumberConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-input-number-config.interface';
import { FormSelectComponent } from '../../../../../../../shared/modules/configurable-form/components/form-select/form-select.component';
import { FieldSelectConfig } from '../../../../../../../shared/modules/configurable-form/components/form-select/models/field-select-config.interface';
import { RmsFormConfig } from '../../../../models/rms-form-config';
import { CutOffEffects } from '../../../../effects/cut-off.effects';
import { FormInfoIconMessageComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-info-icon-message/form-info-icon-message.component';
import {
  FilteredUnloadTimesLoaderService,
  SingleApplicationsLoaderService,
  SinglePumpabilityFilteredLoaderService,
  SingleSlumpsLoaderService,
  SingleSpacingsLoaderUkService,
  UnloadTypeLoaderService,
} from '../../../../services/config-loader/loaders/single-catalogs.loader.service';
import { ProductsOpenPlatformEffects } from '@ordersnproducts/modules/order-builder/modules/rms-specification/effects/open-platform/products.effects';
import { ProductsDisablerOpenPlatformProvider } from '@ordersnproducts/modules/order-builder/modules/rms-specification/providers/open-platform/product-enabler.provider';
import { CapacityProvider } from '../../../../providers/rms-maximum-capacity.provider';
import { QuantityErrorOpenPlatformProvider } from '@ordersnproducts/modules/order-builder/modules/rms-specification/providers/open-platform/quantity-error.provider';
import { SingleCatalogsEffects } from '../../../../effects/single-catalogs.effects';
import { FormInputComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-input/form-input.component';
import { generateAddedValueFormName } from '../../../../helpers/form-added-value-name-generator';
import { AddedValueFieldConfig } from '../../../../models/form-added-value-field-config';

import { MixReferencesOptionsLoaderOpenPlatformService } from '../../../../services/config-loader/loaders/open-platform/products.loader.service';
import {
  LoadFilteredUnloadTypeMixRef,
  LoadSingleCatalogsUnloadTypeMixRef,
} from '../../../../services/config-loader/commands/single-catalogs-unload-type.command.service';
import { LoadSingleCatalogsApplicationMixRef } from '../../../../services/config-loader/commands/single-catalogs-application.command.service';
import { LoadSingleCatalogsUnloadTimeMixRef } from '../../../../services/config-loader/commands/single-catalogs-unload-time.command.service';
import { LoadSingleCatalogsSpacingMixRef } from '../../../../services/config-loader/commands/single-catalogs-spacing.command.service';
import { LoadFilteredCatalogSlumpMixRef } from '../../../../services/config-loader/commands/single-catalogs-slump.command.service';
import { ResetSlotOpenPlatformCommandService } from '../../../../services/config-loader/commands/open-platform/reset-slot.command.service';
import {
  LoadChildAddedValuesMixReferenceCommandService,
  LoadProductAddedValuesMixReferenceCommandService,
} from '../../../../services/config-loader/commands/open-platform/load-added-value-mix-reference.command.service';
import { AddedValuesLoaderProductOpenPlatformService } from '../../../../services/config-loader/loaders/open-platform/added-value.loader.service';
import { LoadContractOpenPlatformCommandService } from '../../../../services/config-loader/commands/open-platform/load-contract.command.service';
import { LoadFilteredPumpability } from '../../../../services/config-loader/commands/single-catalogs-pumpability.command.service';
import { AddedValueOpenPlatformDisabler } from '../../../../providers/open-platform/added-value-enabler.provider';
import { HandleUnloadTypeDefaults } from '../../../../services/config-loader/commands/handle-unloadTypes-defaults.command.service';
import { SpecFieldTestIds } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-test-ids.enum';

export const rmsUL = {
  recipe: [
    {
      type: FormInputComponent,
      label: 'id',
      name: SpecFieldNames.ID,
      containerClassNames: ['d-none'],
      placeholder: 'id',
    } as FieldConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.product_description',
      name: SpecFieldNames.PRODUCT_SELECTION,
      dataTId: SpecFieldTestIds.PRODUCT_DESCRIPTION,
      containerClassNames: ['field-12x field-md-6x retail-control'],
      optionsLoader: MixReferencesOptionsLoaderOpenPlatformService,
      loading: ProductsDisablerOpenPlatformProvider.IsLoading,
      validation: [Validators.required],
      dynamicEffects: [ProductsOpenPlatformEffects, CutOffEffects],
      placeholder: 'views.specifications.select_product',
      onChange: [
        LoadSingleCatalogsUnloadTypeMixRef,
        LoadSingleCatalogsSpacingMixRef,
        LoadSingleCatalogsUnloadTimeMixRef,
        LoadSingleCatalogsApplicationMixRef,
        ResetSlotOpenPlatformCommandService,
        LoadProductAddedValuesMixReferenceCommandService,
        LoadContractOpenPlatformCommandService,
        LoadFilteredCatalogSlumpMixRef,
        LoadFilteredPumpability,
      ],
      rehydrate: true,
    } as FieldSelectConfig,
    {
      type: FormInputNumberComponent,
      label: 'views.specifications.quantity',
      containerClassNames: ['field-11x field-md-2x retail-control'],
      name: SpecFieldNames.QUANTITY,
      dataTId: SpecFieldTestIds.QUANTITY,
      min: 1,
      value: 1,
      step: 0.5,
      max: 1000,
      suffix: UNITS.CUBIC_METERS,
      validation: [Validators.required, Validators.max(1000), Validators.min(1)],
      decimals: 1,
      onChange: [ResetSlotOpenPlatformCommandService],
      errorMessage: QuantityErrorOpenPlatformProvider.ErrorMessage,
    } as FieldInputNumberConfig,
    {
      type: FormInfoIconMessageComponent,
      name: SpecFieldNames.CONCRETE_MIN_QUANTITY_INFO,
      dataTId: SpecFieldTestIds.CONCRETE_MIN_QUANTITY_INFO,
      value: false,
      containerClassNames: ['field-1x field-md-1x retail-control'],
      text: CapacityProvider.MinMaxMessage,
    } as FieldConfig,
    {
      type: null,
      push: true,
    } as NewLineField,

    {
      type: null,
      push: true,
    } as NewLineField,
    {
      type: FormSelectComponent,
      label: 'views.specifications.valueAdded',
      name: generateAddedValueFormName(0),
      dataTId: SpecFieldTestIds.VALUE_ADDED_FIRST,
      index: 0,
      loading: AddedValueOpenPlatformDisabler.IsLoading,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_valueAdded',
      optionsLoader: AddedValuesLoaderProductOpenPlatformService,
      onChange: [
        LoadChildAddedValuesMixReferenceCommandService,
        LoadFilteredCatalogSlumpMixRef,
        LoadFilteredPumpability,
      ],
      rehydrate: true,
    } as AddedValueFieldConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.valueAdded',
      name: generateAddedValueFormName(1),
      dataTId: SpecFieldTestIds.VALUE_ADDED_SECOND,
      index: 1,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_valueAdded',
      optionsLoader: AddedValuesLoaderProductOpenPlatformService,
      loading: AddedValueOpenPlatformDisabler.IsLoading,
      onChange: [
        LoadChildAddedValuesMixReferenceCommandService,
        LoadFilteredCatalogSlumpMixRef,
        LoadFilteredPumpability,
      ],
      rehydrate: true,
    } as AddedValueFieldConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.valueAdded',
      name: generateAddedValueFormName(2),
      dataTId: SpecFieldTestIds.VALUE_ADDED_THIRD,
      index: 2,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_valueAdded',
      optionsLoader: AddedValuesLoaderProductOpenPlatformService,
      loading: AddedValueOpenPlatformDisabler.IsLoading,
      onChange: [
        LoadChildAddedValuesMixReferenceCommandService,
        LoadFilteredCatalogSlumpMixRef,
        LoadFilteredPumpability,
      ],
      rehydrate: true,
    } as AddedValueFieldConfig,
    {
      type: null,
      push: true,
    } as NewLineField,
    {
      type: FormSelectComponent,
      label: 'views.specifications.slump',
      name: SpecFieldNames.SLUMP,
      dataTId: SpecFieldTestIds.SLUMP,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_slump',
      optionsLoader: SingleSlumpsLoaderService,
      validation: [Validators.required],
      dynamicEffects: [SingleCatalogsEffects],
      onChange: [LoadFilteredPumpability, ResetSlotOpenPlatformCommandService],
    } as FieldSelectConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.pumpability',
      name: SpecFieldNames.PUMPABILITY,
      dataTId: SpecFieldTestIds.PUMPABILITY,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_pumpability',
      optionsLoader: SinglePumpabilityFilteredLoaderService,
      validation: [Validators.required],
      onChange: [LoadFilteredCatalogSlumpMixRef, LoadFilteredUnloadTypeMixRef, ResetSlotOpenPlatformCommandService],
    } as FieldSelectConfig,
    {
      type: null,
      push: true,
    } as NewLineField,
    {
      type: FormSelectComponent,
      label: 'views.specifications.unload_type',
      name: SpecFieldNames.UNLOAD_TYPE,
      dataTId: SpecFieldTestIds.UNLOAD_TYPE,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_unloadType',
      optionsLoader: UnloadTypeLoaderService,
      validation: [Validators.required],
      onChange: [HandleUnloadTypeDefaults, ResetSlotOpenPlatformCommandService],
    } as FieldSelectConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.application',
      name: SpecFieldNames.APPLICATION,
      dataTId: SpecFieldTestIds.APPLICATION,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_application',
      optionsLoader: SingleApplicationsLoaderService,
      validation: [Validators.required],
      onChange: [ResetSlotOpenPlatformCommandService],
    } as FieldSelectConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.unloadTime',
      name: SpecFieldNames.DISCHARGE_TIME,
      dataTId: SpecFieldTestIds.UNLOAD_TIME,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_unloadTime',
      optionsLoader: FilteredUnloadTimesLoaderService,
      validation: [Validators.required],
      onChange: [ResetSlotOpenPlatformCommandService],
    } as FieldSelectConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.spacing',
      name: SpecFieldNames.SPACING,
      dataTId: SpecFieldTestIds.SPACING,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_spacing',
      optionsLoader: SingleSpacingsLoaderUkService,
      validation: [Validators.required],
      dynamicEffects: [SingleCatalogsEffects],
      onChange: [ResetSlotOpenPlatformCommandService],
    } as FieldSelectConfig,
    {
      type: null,
      push: true,
    } as NewLineField,
    {
      type: FormInputCheckboxComponent,
      name: SpecFieldNames.ADJUSTMENT,
      dataTId: SpecFieldTestIds.ADJUSTMENT,
      value: false,
      containerClassNames: ['field-6x field-md-2x retail-control'],
      placeholder: 'views.specifications.kicker',
    } as FieldConfig,
  ],
} as RmsFormConfig;
